import {
  completeFromList,
  CompletionContext,
  CompletionSource,
  ifNotIn,
} from '@codemirror/autocomplete';
import { javascript } from '@codemirror/lang-javascript';
import { SQLConfig } from '@codemirror/lang-sql';
import { LanguageSupport, LRLanguage } from '@codemirror/language';
import { Prec } from '@codemirror/state';
import {
  Decoration,
  DecorationSet,
  EditorView,
  MatchDecorator,
  ViewPlugin,
  ViewUpdate,
} from '@codemirror/view';
import { parseMixed } from '@lezer/common';
import {
  dialect,
  parser as sqlTemplatedParser,
  SQLiteTemplated,
  Terms,
} from '@persona-id/codemirror-lang-sql-templated';

// Extracted out of @codemirror/lang-sql
function completeKeywords(keywords: { [name: string]: number }, upperCase: boolean) {
  const completions = Object.keys(keywords).map((keyword) => ({
    label: upperCase ? keyword.toUpperCase() : keyword,
    type:
      keywords[keyword] === Terms.Type
        ? 'type'
        : keywords[keyword] === Terms.Keyword
          ? 'keyword'
          : 'variable',
    boost: -1,
  }));
  return ifNotIn(
    ['QuotedIdentifier', 'SpecialVar', 'String', 'LineComment', 'BlockComment', '.'],
    completeFromList(completions),
  );
}

// We create our own sql dialect that is an extension of @codemirror/lang-sql
// which defines it's own grammar that has support for directives.
export function sqlTemplated(
  config: SQLConfig,
  sqlCompletionSources: CompletionSource[] = [],
  jsCompletionSources: CompletionSource[] = [],
) {
  const sqliteSpec = SQLiteTemplated.spec;
  const d = dialect(sqliteSpec, sqliteSpec.keywords, sqliteSpec.types, sqliteSpec.builtin);
  const javascriptLang = javascript();

  const jsCompletionExtensions = jsCompletionSources.map((source) => {
    return javascriptLang.language.data.of({
      autocomplete: source,
    });
  });

  const parser = sqlTemplatedParser.configure({
    // tokenizers: [{ from: tokens, to: tokensFor(d) }],
    wrap: parseMixed((node) => {
      return node.type.isTop
        ? {
            parser: javascriptLang.language.parser,
            overlay: (node) => node.type.name === 'Directive',
          }
        : null;
    }),
  });

  const language = LRLanguage.define({
    name: 'sqljs',
    parser: parser,
    // languageData taken from @codemirror/lang-sql
    languageData: {
      commentTokens: { line: '--', block: { open: '/*', close: '*/' } },
      closeBrackets: { brackets: ['(', '[', '{', "'", '"', '`'] },
    },
  });

  const scriptDecorator = new MatchDecorator({
    regexp: /{{.*}}/g,
    decoration: Decoration.mark({ class: 'cm-persona-script-embed' }),
  });

  const scriptHighlight = ViewPlugin.fromClass(
    class {
      decorations: DecorationSet;
      constructor(view: EditorView) {
        this.decorations = scriptDecorator.createDeco(view);
      }
      update(update: ViewUpdate) {
        this.decorations = scriptDecorator.updateDeco(update, this.decorations);
      }
    },
    {
      decorations: (v) => v.decorations,
    },
  );

  const bracesDecorator = new MatchDecorator({
    regexp: /{{|}}/g,
    decoration: Decoration.mark({ class: 'cm-persona-script-embed-braces' }),
  });

  const bracesHighlight = ViewPlugin.fromClass(
    class {
      decorations: DecorationSet;
      constructor(view: EditorView) {
        this.decorations = bracesDecorator.createDeco(view);
      }
      update(update: ViewUpdate) {
        this.decorations = bracesDecorator.updateDeco(update, this.decorations);
      }
    },
    {
      decorations: (v) => v.decorations,
    },
  );

  const languageSupport = () =>
    new LanguageSupport(
      language,
      [
        [
          language.data.of({
            autocomplete: (context: CompletionContext) => {
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              return completeKeywords(d.words, config.upperCaseKeywords)(context);
            },
          }),
          ...sqlCompletionSources.map((source) => {
            return language.data.of({
              autocomplete: source,
            });
          }),
        ],
        [javascriptLang.support, ...jsCompletionExtensions],
        Prec.lowest([bracesHighlight, scriptHighlight]),
      ].flat(),
    );

  return [languageSupport()];
}
